import React from 'react';

// ===================================
// SHARED TYPES & UTILITIES
// ===================================

interface IconProps {
  className?: string;
}

interface LoadingSpinnerProps extends IconProps {
  size?: 'sm' | 'md' | 'lg';
  color?: string;
}

// Common SVG wrapper for consistent styling
const SvgIcon: React.FC<{ className?: string; children: React.ReactNode }> = ({ 
  className = "h-4 w-4", 
  children 
}) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    {children}
  </svg>
);

// Common path component for stroke-based icons
const StrokePath: React.FC<{ d: string }> = ({ d }) => (
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
);

// ===================================
// LOADING SPINNER
// ===================================

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'md', 
  color = 'text-gray-500',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5', 
    lg: 'h-8 w-8'
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]} ${color} ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

// ===================================
// SVG ICONS
// ===================================

export const DashboardIcon: React.FC<IconProps> = ({ className }) => (
  <SvgIcon className={className}>
    <StrokePath d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </SvgIcon>
);

export const UsersIcon: React.FC<IconProps> = ({ className }) => (
  <SvgIcon className={className}>
    <StrokePath d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
  </SvgIcon>
);

export const VideoIcon: React.FC<IconProps> = ({ className }) => (
  <SvgIcon className={className}>
    <StrokePath d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </SvgIcon>
);

export const LogoutIcon: React.FC<IconProps> = ({ className }) => (
  <SvgIcon className={className}>
    <StrokePath d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </SvgIcon>
);

export const ChevronDownIcon: React.FC<IconProps & { isOpen?: boolean }> = ({ 
  className = "w-4 h-4", 
  isOpen = false 
}) => (
  <svg
    className={`${className} transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <StrokePath d="M19 9l-7 7-7-7" />
  </svg>
);

export const ErrorIcon: React.FC<IconProps> = ({ className = "h-6 w-6" }) => (
  <SvgIcon className={className}>
    <StrokePath d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </SvgIcon>
);

// Unused icons removed: CloseIcon, CheckIcon, DeleteIcon, SearchIcon, TimeIcon, GroupIcon, DocumentIcon
// If you need these icons, use Material-UI icons instead:
// import { Close, Check, Delete, Search, Schedule, Group, Description } from '@mui/icons-material';
